<template>
  <div>
    <v-row v-if="!isNewMode&&formModel.isUpdatedCassationRequest && formModel.expireReplyDate">
      <v-col cols="12">
        <naj-alert type="information">
          <template slot="content">
            {{ local.caseRelatedRequest.cassationRequest.expireReplyDate }} {{ formModel.expireReplyDate || '-' }}
          </template>
        </naj-alert>
      </v-col>
    </v-row>
    <v-row v-if="!isNewMode&&formModel.isUpdatedCassationRequest && formModel.noReplies">
      <v-col cols="12">
        <naj-alert type="information">
          <template slot="content">
            {{ local.caseRelatedRequest.cassationRequest.noReplies }}
          </template>
        </naj-alert>
      </v-col>
    </v-row>
    <!-- In new mode the judgment-selection step is rendered by cassation-request-switcher, above the variant
         boundary, so a variant is never on screen before the judgment that resolves it has been chosen. -->
    <naj-box
      v-if="!isNewMode"
      :title="local.caseRelatedRequest.cassationRequest.cassationRequestTitle"
    >
      <v-row>
        <v-col cols="6">
          <naj-input
            :type-id="formFieldType.text"
            :label="local.caseRelatedRequest.cassationRequest.judgementCode"
            :value.sync="formModel.judgementCode"
            :disabled="!isNewMode"
            validates="required"
          ></naj-input>
        </v-col>
      </v-row>
    </naj-box>

    <naj-box
      v-if="isUpdatedCassationRequest"
      :title="local.caseRelatedRequest.cassationRequest.partyAgainstTitle"
    >
      <v-row v-if="isNewMode">
        <v-col cols="6">
          <naj-alert type="information">
            <template slot="content">
              {{ local.caseRelatedRequest.cassationRequest.objectedPartiesHint }}
            </template>
          </naj-alert>
        </v-col>
      </v-row>
      <v-row v-if="isNewMode">
        <v-col
          cols="12"
          class="d-flex justify-start"
        >
          <naj-button
            icon="mdi-plus"
            :disabled="isAddObjectedPartyDisabled"
            @click="showObjectedPartiesModal = true"
          >
            {{ local.common.add }}
          </naj-button>
        </v-col>
      </v-row>
      <v-row v-if="objectedPartiesTableData.length > 0">
        <v-col cols="12">
          <naj-data-table
            :headers="isViewMode ? objectedPartiesHeaders : objectedPartiesHeadersWithAction"
            :items="objectedPartiesTableData"
            :disable-items-per-page="true"
          >
            <template slot-scope="{ item }">
              <v-btn
                v-if="isNewMode && !isViewMode && !isEditMode"
                icon
                @click="removeObjectedParty(item)"
              >
                <v-icon color="red">
                  mdi-delete
                </v-icon>
              </v-btn>
            </template>
          </naj-data-table>
        </v-col>
      </v-row>
    </naj-box>

    <objected-parties-modal
      :modal-visibility="showObjectedPartiesModal"
      :all-parties="availableCaseParties"
      :selected-party-ids="formModel.objectionedPartiesIds || []"
      @save="onObjectedPartiesSaved"
      @close="showObjectedPartiesModal = false"
    ></objected-parties-modal>

    <naj-box
      v-if="isUpdatedCassationRequest && formModel.judgementCode"
      :title="local.caseRelatedRequest.cassationRequest.cassationReasons"
    >
      <v-row>
        <v-col
          v-if="isNewMode"
          cols="1"
          class="mb-6"
        >
          <naj-button
            :disabled="isDraftExist || editedReasonIndex !== -1"
            @click="handleAddReason"
          >
            {{ local.caseRelatedRequest.cassationRequest.addReason }}
          </naj-button>
        </v-col>
      </v-row>

      <naj-box v-if="isDraftExist">
        <v-row>
          <v-col cols="12">
            <template>
              <cassation-reason-form
                :reason="newReason"
                :available-reasons="availableReasons"
                :disabled="false"
                :selected-judgment="selectedJudgment"
                :form-model="formModel"
                @add="appendReason"
                @cancel="discardReason"
              >
              </cassation-reason-form>
            </template>
          </v-col>
        </v-row>
      </naj-box>

      <!-- Display New Reasons -->
      <template v-if="formModel.cassationReasons && formModel.cassationReasons.length">
        <naj-box
          v-for="(reason, index) in formModel.cassationReasons"
          :key="index"
          :title="reason.label"
        >
          <template>
            <!-- View Mode -->
            <cassation-reason-form
              :reason="reason"
              :available-reasons="availableReasons"
              :disabled="isReasonEdited(index)"
              :selected-judgment="selectedJudgment"
              :form-model="formModel"
              @add="saveEditedReason(index)"
              @cancel="cancelEditReason"
            >
            </cassation-reason-form>

            <v-row v-if="!isNewMode">
              <v-col
                v-if="!isNewMode && formModel.canReply"
                cols="12"
              >
                <naj-input
                  :type-id="formFieldType.textarea"
                  :label="local.caseRelatedRequest.cassationRequest.reply"
                  :value.sync="reason.reply.text"
                  validates="required|max:6000|allowed_special_character"
                  rows="5"
                  @input="handleReplyTextChange(reason.sectionId, reason.reply.text)"
                ></naj-input>
              </v-col>
            </v-row>
            <v-row>
              <v-col
                v-if="!isNewMode"
                cols="12"
              >
                <label class="request-reason-label">{{ local.caseRelatedRequest.cassationRequest.fullReplyText }}</label>
                <div v-if="!isNewMode && formModel.cassationReasonsReplies.length > 0">
                  <v-tabs
                    v-model="reasonTabs[reason.sectionId]"
                    align-tabs="end"
                    class="mt-7"
                    background-color="white"
                    slider-color="var(--app-primary-color)"
                    active-class="tab-active"
                  >
                    <v-tab
                      v-for="(reply,n) in formModel.cassationReasonsReplies.filter((x) => x.sectionId == reason.sectionId)"
                      :key="`tab-header-${n}`"
                      :href="`#tab-${n}`"
                      class="tab-padding"
                    >
                      {{ reply.name }}
                    </v-tab>
                  </v-tabs>
                  <v-tabs-items v-model="reasonTabs[reason.sectionId]">
                    <v-tab-item
                      v-for="(reply,n) in formModel.cassationReasonsReplies.filter((x) => x.sectionId == reason.sectionId)"
                      :key="`tab-body-${n}`"
                      :value="`tab-${n}`"
                    >
                      <naj-input
                        :ref="`tab-${n}`"
                        readonly
                        :type-id="formFieldType.textarea"
                        :value.sync="reply.replyText"
                        rows="5"
                        validates="max:6000"
                      ></naj-input>
                    </v-tab-item>
                  </v-tabs-items>
                </div>
                <div v-else>
                  <span class="request-reason-text">{{ local.caseRelatedRequest.cassationRequest.noReply }}</span>
                </div>
              </v-col>
            </v-row>

            <v-row v-if="isNewMode && editedReasonIndex == -1 && !isDraftExist">
              <naj-button
                icon="mdi-delete"
                color="error"
                @click="handleRemoveReason(index)"
              >
                {{ local.common.delete }}
              </naj-button>

              <naj-button
                icon="mdi-pencil"
                type="button"
                size="small"
                @click="handleEditReason(index)"
              >
                {{ local.common.update }}
              </naj-button>
            </v-row>
          </template>
        </naj-box>
      </template>
    </naj-box>
    <naj-box
      v-else-if="formModel.judgementCode"
      :title="local.caseRelatedRequest.cassationRequest.reasons"
    >
      <template v-for="(reason,index) in (formModel.cassationReasons||[])">
        <v-row :key="index">
          <v-col cols="12">
            <naj-input
              :type-id="formFieldType.checkbox"
              :value.sync="reason.checked"
              :label="reason.label"
              :disabled="isViewMode"
              @change="reasonCheckChanged(index)"
            ></naj-input>
          </v-col>
          <v-col
            v-if="reason.checked"
            cols="6"
            lg="6"
          >
            <naj-input
              :type-id="formFieldType.textarea"
              :value.sync="reason.description"
              label=""
              :disabled="isViewMode"
              validates="required|max:2000|allowed_special_character"
              rows="4"
            ></naj-input>
          </v-col>
        </v-row>
      </template>
    </naj-box>
    <naj-box
      v-if="isNewMode || (!isNewMode && formModel.cassationFile)"
      :title="local.caseRelatedRequest.cassationRequest.cassationRequestAttachment"
    >
      <v-row>
        <naj-alert type="warning">
          <template slot="content">
            {{ local.caseRelatedRequest.cassationRequest.attachmentAlert }}
          </template>
        </naj-alert>
      </v-row>
      <v-row>
        <v-col
          v-if="formModel.cassationFile"
          cols="3"
        >
          <p class="objection-judgment-confirm-text">
            {{ local.caseRelatedRequest.cassationRequest.cassationRequestAttachment }}
          </p>
          <attachment-card
            :file="{ ...uploadedFile }"
            :show-delete-button="isNewMode"
            @deleteAttachment="showDeleteObjectionNoteModal"
            @viewAttachment="viewAttachment"
          ></attachment-card>
        </v-col>
        <v-col
          v-else
          class="objection-note-attachment"
          cols="12"
        >
          <p class="objection-judgment-confirm-text blackColor">
            {{ local.caseRelatedRequest.cassationRequest.addCassationRequestAttachment }}
          </p>
          <load-local-attachment
            :max-file-size="5"
            :upload-disabled="isViewMode"
            @fileSelected="fileSelected"
          >
          </load-local-attachment>
        </v-col>
      </v-row>
    </naj-box>

    <case-judgement-modal
      :selected-judgement="selectedJudgment"
      :modal-visibility="showCaseJudgementModal"
      @modalClose="closeCaseJudgementModal"
    ></case-judgement-modal>

    <render-attachment-modal
      :attachment-file="uploadedFile"
      :modal-visibility="showViewAttachmentModal"
      @modalClose="hideViewAttachmentModal"
    >
    </render-attachment-modal>

    <render-attachment-modal
      :attachment-file="uploadedFileStopExecution"
      :modal-visibility="showViewStopExecutionAttachmentModal"
      @modalClose="hideViewStopExecutionAttachmentModal"
    >
    </render-attachment-modal>

    <naj-confirmation
      :show-modal="showDeleteObjectionNoteAttachment"
      color="error"
      :title="local.common.removeConfirmation"
      :message="local.caseRelatedRequest.VerdictObjectionRequest.removeObjectionNoteAttachmentMessage"
      :ok-btn-label="local.common.remove"
      @ok="confirmDeleteObjectionNote"
      @cancel="cancelDeleteObjectionNote"
    ></naj-confirmation>

    <naj-confirmation
      :show-modal="showDeleteObjectionStopExecutionAttachment"
      color="error"
      :title="local.common.removeConfirmation"
      :message="local.caseRelatedRequest.cassationRequest.removeObjectionStopExecutionAttachmentMessage"
      :ok-btn-label="local.common.remove"
      @ok="confirmDeleteObjectionStopExecution"
      @cancel="cancelDeleteObjectionStopExecution"
    ></naj-confirmation>

    <naj-alert-modal
      :show-modal="showAlertModalForCriminalCases"
      type="information"
      :title="local.common.criminalAttachmentTitle"
      :message="local.common.criminalAttachment"
      @onCancelModal="showAlertModalForCriminalCases = false"
    >
    </naj-alert-modal>
  </div>
</template>

<script>
import { yesOrNoEnumBoolean, mode, socialIdentityTypeEnum, countriesEnum, yesOrNoEnumBooleanMap } from '@/helpers/enum';
import { createNamespacedHelpers } from 'vuex';
const { mapState: mapLUTState, mapActions: mapLUTActions } = createNamespacedHelpers('LookupsModule');
const { mapState: mapGlobalState } = createNamespacedHelpers('GlobalModule');
const { mapActions } = createNamespacedHelpers('CasesModule');
import CaseJudgementModal from '../../../../cases/case-judgement-modal';
import LoadLocalAttachment from '../../../../../components/shared/attachment-modal/load-local-attachment';
import AttachmentCard from '@/components/shared/attachment-modal/attachment-card';
import RenderAttachmentModal from '@/components/shared/attachment-modal/render-attachment-modal';
import CassationReasonForm from './cassation-reason-form';
import ObjectedPartiesModal from './objected-parties-modal';
import { uuidGenerator } from '@/helpers/utils';
export default {
  name: 'CassationRequest',
  inject: ['$validator'],
  components: {
    CaseJudgementModal,
    LoadLocalAttachment,
    AttachmentCard,
    RenderAttachmentModal,
    CassationReasonForm,
    ObjectedPartiesModal,
  },
  props: {
    formModel: {
      type: Object,
      default: () => ({}),
    },
    requiredData: {
      type: Object,
      default: () => ({}),
    },
    loadedJudgments: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      yesOrNoEnumBoolean,
      yesOrNoEnumBooleanMap,
      judgments: [],
      newReason: {
        reasonId: null,
        label: '',
        postionOfJudgementText: '',
        statementOfViolation: '',
        violationEffect: '',
        hasObjectionedForAppeal: null,
        reasonForObjectionWithSameReasonAgain: '',
        reasonForNotMentionAtAppeal: '',
        subject: {
          judgementSectionId: 0,
          judgementHighlightedText: '',
          judgementSectionName: '',
        },
        reply: { text: '' },
        sectionId: uuidGenerator(),
      },
      showCaseJudgementModal: false,
      showDeleteObjectionNoteAttachment: false,
      showDeleteObjectionStopExecutionAttachment: false,
      showViewAttachmentModal: false,
      showViewStopExecutionAttachmentModal: false,
      showAlertModalForCriminalCases: false,
      showObjectedPartiesModal: false,
      selectedJudgment: {},
      availableCaseParties: [],
      userCasePartyRoleInCase: {},
      objectedPartiesHeaders: [
        {
          text: this.local.caseRelatedRequest.cassationRequest.objectedPartiesTable.partyName,
          value: 'name',
        },
        {
          text: this.local.caseRelatedRequest.cassationRequest.objectedPartiesTable.partyRole,
          value: 'casePartyRoleName',
        },
      ],
      objectedPartiesHeadersWithAction: [
        {
          text: this.local.caseRelatedRequest.cassationRequest.objectedPartiesTable.partyName,
          value: 'name',
        },
        {
          text: this.local.caseRelatedRequest.cassationRequest.objectedPartiesTable.partyRole,
          value: 'casePartyRoleName',
        },
        {
          text: this.local.caseRelatedRequest.cassationRequest.objectedPartiesTable.action,
          value: 'action',
          sortable: false,
        },
      ],
      isDraftExist: false,
      replies: [],
      reasonTabs: {},
      tabs: null,
      editedReasonIndex: -1,
      editedReasonBackup: null,
      isCommercialCourtJudgement: false,
      lastValidatedJudgementCode: null,
      lastLoadedCasePartiesJudgementCode: null,
    };
  },
  computed: {
    ...mapLUTState(['cassationObjectionReasons', 'yesOrNo', 'cassationRequestReasons']),
    ...mapGlobalState(['userInfo']),
    reasons() {
      return (this.cassationRequestReasons || []).map(item => ({
        reasonId: item.id,
        label: item.name,
        checked: false,
        description: '',
      }));
    },
    objectedPartiesTableData() {
      if (!this.formModel.objectionedPartiesIds || this.formModel.objectionedPartiesIds.length === 0) {
        this.$set(this.formModel, 'objectedPartiesTableData', []);
        return [];
      }
      if (this.isNewMode) {
        const result = this.availableCaseParties
          .filter(party => this.formModel.objectionedPartiesIds.includes(party.casePartyId))
          .map(party => ({
            casePartyId: party.casePartyId,
            name: party.name,
            casePartyRoleName: party.casePartyRoleName,
          }));
        this.$set(this.formModel, 'objectedPartiesTableData', [...result]);
        return result;
      }
      return this.formModel.objectedPartiesTableData || [];
    },
    isUpdatedCassationRequest() {
      return this.formModel.isUpdatedCassationRequest;
    },
    isViewMode() {
      return this.formModel && this.formModel.mode === mode.view;
    },
    isEditMode() {
      return this.formModel && this.formModel.mode === mode.update;
    },
    isNewMode() {
      return this.$route.params.requestObjectKey === 'new';
    },
    uploadedFile() {
      if (this.formModel.cassationFile && this.formModel.cassationFile.fileData) {
        const fileData = this.formModel.cassationFile.fileData;
        return {
          name: fileData.fileName,
          attachmentKey: fileData.attachmentKey,
          sharePointId: fileData.attachement,
          isImage: fileData.fileType.startsWith('image'),
          fileType: fileData.fileType,
        };
      }
      return {};
    },
    uploadedFileStopExecution() {
      if (this.formModel.stopExecutionAttachment && this.formModel.stopExecutionAttachment.fileData) {
        const fileData = this.formModel.stopExecutionAttachment.fileData;
        return {
          name: fileData.fileName,
          attachmentKey: fileData.attachmentKey,
          sharePointId: fileData.attachement,
          isImage: fileData.fileType.startsWith('image'),
          fileType: fileData.fileType,
        };
      }
      return {};
    },
    routeRequestObjectKey() {
      return this.$route.query.requestObjectKey;
    },
    isAddObjectedPartyDisabled() {
      return this.isViewMode || this.isEditMode || !this.formModel.judgementCode || this.availableCaseParties.length === 0;
    },
    availableReasons() {
      if (this.isCommercialCourtJudgement) {
        return this.reasons;
      }
      return this.reasons.filter(r => r.reasonId !== 80);
    },
  },
  watch: {
    'formModel.judgementCode'(newVal, oldVal) {
      if (newVal === oldVal) {
        return;
      }

      if (this.isNewMode) {
        // reset dynamically-added reasons when judgement changes in new form
        this.$set(this.formModel, 'cassationReasons', []);
        this.isDraftExist = false;
        this.editedReasonIndex = -1;
        this.editedReasonBackup = null;
      }

      this.setSelectedJudgment();
    },
  },
  mounted() {
    this.getCassationRequestReasons();
    const params = {
      isPega: this.formModel.isPega,
      objectKey: this.formModel.case.objectKey,
    };
    if (this.formModel) {
      if (
        this.formModel.cassationReasons &&
        !this.formModel.isUpdatedCassationRequest &&
        this.formModel.cassationReasons.length > 0
      ) {
        this.formModel.cassationReasons.forEach((element) => {
          let item = this.reasons.find(a => a.reasonId == element.reasonId);
          if (item) {
            this.$set(item, 'description', element.description);
            this.$set(item, 'checked', element.checked);
          }
        });
      }
      if (this.isNewMode) {
        this.$set(this.formModel, 'isUpdatedCassationRequest', true);
        this.$set(this.formModel, 'cassationReasons', []);
      }
      if (this.isUpdatedCassationRequest && this.formModel.canReply && this.formModel.cassationReasons.length > 0) {
        if (!Array.isArray(this.formModel.replies)) {
          this.$set(this.formModel, 'replies', []);
        }
        this.formModel.cassationReasons.forEach((element) => {
          this.formModel.replies.push({
            sectionId: element.sectionId,
            casePartyId: this.formModel.casePartyId,
            representativePartyId: this.formModel.representativeCasePartyId,
            replyText: '',
          });
        });
      }
      if (this.formModel.judgementCode) {
        this.setSelectedJudgment();
      }
      this.readCurrentUserInfo();
    }

    if ((this.loadedJudgments || []).length > 0) {
      this.judgments = [...this.loadedJudgments];
      this.setSelectedJudgment();
      return;
    }

    this.loading = true;

    if (this.routeRequestObjectKey || this.requiredData?.isFromNonCaseParty) {
      this.getJudgementsByRequestObjectKey(
        this.routeRequestObjectKey ? this.routeRequestObjectKey : this.requiredData?.parentRequestObjectKey
      )
        .then((res) => {
          this.judgments = res.data;
          this.setSelectedJudgment();
        })
        .finally(() => (this.loading = false));
    } else {
      this.getCaseJudgments(params)
        .then((res) => {
          this.judgments = res.data;
          this.setSelectedJudgment();
        })
        .finally(() => (this.loading = false));
    }
  },
  methods: {
    ...mapLUTActions(['getCassationRequestReasons']),
    ...mapActions([
      'getCaseJudgments',
      'uploadAttachment',
      'isValidApplicantForCommercialJudgement',
      'isJudgementFromCommercialCourt',
      'getJudgementsByRequestObjectKey',
      'getCaseRequestApplicantsByJudgement',
      'getUserCasePartyRoleInCase',
    ]),
    loadCaseParties(judgementCode) {
      this.loading = true;
      this.getCaseRequestApplicantsByJudgement(judgementCode)
        .then((res) => {
          const requestApplicants = res.data.requestApplicants || [];
          const prosecutors = res.data.prosecutors || [];
          const defendants = res.data.defendants || [];
          // concat all prosecutors and defendants
          const allParties = [...prosecutors, ...defendants];

          // get requestApplicant casePartyIds for exclusion
          const requestApplicantIds = requestApplicants.map(ra => ra.casePartyId);

          // exclude parties that are in requestApplicants
          const filteredParties = allParties.filter(party => !requestApplicantIds.includes(party.casePartyId));

          const mapPartyToOption = party => ({
            casePartyId: party.casePartyId,
            name: party.name,
            casePartyRoleName: party.casePartyRoleName,
          });
          // map to availableCaseParties format
          if (this.isNewMode) {
            this.availableCaseParties = filteredParties.map(mapPartyToOption);
          } else {
            const objectedParties = allParties.filter(party =>
              this.formModel.objectionedPartiesIds.includes(party.casePartyId)
            );
            this.availableCaseParties = objectedParties.map(mapPartyToOption);
          }
        })
        .catch(() => {
          this.availableCaseParties = [];
        })
        .finally(() => (this.loading = false));
    },
    removeObjectedParty(item) {
      if (this.isViewMode) return;
      const index = this.formModel.objectionedPartiesIds.indexOf(item.casePartyId);
      if (index > -1) {
        this.formModel.objectionedPartiesIds.splice(index, 1);
      }
    },
    onObjectedPartiesSaved(selectedIds) {
      this.$set(this.formModel, 'objectionedPartiesIds', selectedIds);
      this.showObjectedPartiesModal = false;
    },
    isReasonEdited(index) {
      return this.editedReasonIndex !== index;
    },
    handleAddReason() {
      this.isDraftExist = true;
      this.newReason = {
        reasonId: null,
        label: '',
        postionOfJudgementText: '',
        statementOfViolation: '',
        violationEffect: '',
        hasObjectionedForAppeal: null,
        reasonForObjectionWithSameReasonAgain: '',
        reasonForNotMentionAtAppeal: '',
        subject: null,
        reply: { text: '' },
        sectionId: uuidGenerator(),
      };
    },
    appendReason(item) {
      if (!Array.isArray(this.formModel.cassationReasons)) {
        this.$set(this.formModel, 'cassationReasons', []);
      }
      const reasonWithReply = {
        ...item,
        reply: { text: '' },
      };
      this.formModel.cassationReasons.push(reasonWithReply);
      this.isDraftExist = false;
      this.editedReasonIndex = -1;
    },
    discardReason() {
      this.isDraftExist = false;
    },
    handleRemoveReason(index) {
      this.formModel.cassationReasons.splice(index, 1);
      this.toaster.success(this.local.common.deleteSuccess || 'تم الحذف بنجاح');
    },
    handleEditReason(index) {
      this.editedReasonBackup = JSON.parse(JSON.stringify(this.formModel.cassationReasons[index]));
      this.editedReasonIndex = index;
      this.setSelectedJudgment();
    },
    saveEditedReason(index) {
      this.editedReasonBackup = null;
      this.editedReasonIndex = -1;
    },
    cancelEditReason() {
      if (this.editedReasonIndex !== -1 && this.editedReasonBackup) {
        this.$set(this.formModel.cassationReasons, this.editedReasonIndex, this.editedReasonBackup);
      }
      this.editedReasonBackup = null;
      this.editedReasonIndex = -1;
    },
    handleReplyTextChange(sectionId, replyText) {
      const reply = this.formModel.replies.find(r => r.sectionId === sectionId);
      if (reply) {
        reply.replyText = replyText;
      }
    },
    getMappedIndexReasonText(index) {
      switch (index) {
        case 0:
          return this.local.caseRelatedRequest.cassationRequest.reasonText.first;
        case 1:
          return this.local.caseRelatedRequest.cassationRequest.reasonText.second;
        case 2:
          return this.local.caseRelatedRequest.cassationRequest.reasonText.third;
        case 3:
          return this.local.caseRelatedRequest.cassationRequest.reasonText.fourth;
        case 4:
          return this.local.caseRelatedRequest.cassationRequest.reasonText.fifth;
        default:
          return `السبب ${index + 1}`;
      }
    },
    reasonCheckChanged(index) {
      const selectedReason = this.formModel.cassationReasons[index];
      if (!selectedReason.checked) {
        this.$set(this.formModel.cassationReasons[index], 'description', '');
      }
    },
    setSelectedJudgment() {
      const judgementCode = this.formModel && this.formModel.judgementCode;

      judgementCode &&
        this.$set(
          this,
          'selectedJudgment',
          (this.judgments || []).find(a => a.judgementCode == judgementCode) || {}
        );

      if (this.isNewMode) {
        this.availableCaseParties = [];
        this.$set(this.formModel, 'objectionedPartiesIds', []);
        if (judgementCode && this.lastLoadedCasePartiesJudgementCode !== judgementCode) {
          this.lastLoadedCasePartiesJudgementCode = judgementCode;
          this.loadCaseParties(judgementCode);
        }
      }

      if (judgementCode !== undefined) {
        if (this.lastValidatedJudgementCode === judgementCode) {
          return;
        }

        this.lastValidatedJudgementCode = judgementCode;
        this.loading = true;
        const promises = [
          this.isJudgementFromCommercialCourt(judgementCode)
            .then(({ data }) => {
              this.isCommercialCourtJudgement = !!data;
            })
            .catch(() => {
              this.isCommercialCourtJudgement = false;
            }),
        ];
        if (this.isNewMode) {
          promises.push(
            this.isValidApplicantForCommercialJudgement(judgementCode)
              .then(() => {
                this.$set(this.formModel, 'isApplicantEligibleToApply', true);
              })
              .catch(() => {
                this.$set(this.formModel, 'isApplicantEligibleToApply', false);
              })
          );
        }
        Promise.all(promises).finally(() => {
          this.loading = false;
        });
      } else {
        this.lastValidatedJudgementCode = null;
        this.lastLoadedCasePartiesJudgementCode = null;
        this.$set(this.formModel, 'isApplicantEligibleToApply', undefined);
        this.isCommercialCourtJudgement = false;
      }
    },
    getJudgementDeed() {
      this.showCaseJudgementModal = true;
    },
    closeCaseJudgementModal() {
      this.showCaseJudgementModal = false;
    },
    fileSelected({ selectedAttachment: attachment, selectedAttachmentKey }) {
      this.loading = true;
      const reader = new FileReader();
      reader.onloadend = () => {
        var b64 = reader.result.replace(/^data:.+;base64,/, '');

        this.uploadAttachment({
          classification: 0,
          documentMetadata: {
            caseId: 0,
            caseCode: 0,
            requestId: 0,
            category: attachment.category,
            fileCategory: attachment.category,
          },
          fileCategory: attachment.category,
          extension: attachment.extension,
          base64FileContent: b64,
        })
          .then(({ data }) => {
            this.$set(this.formModel, 'cassationFile', {
              fileData: {
                attachement: data,
                fileName: attachment.name,
                fileType: attachment.fileObject.type,
                sharePointId: data,
                extension: attachment.extension,
                fileCategory: attachment.category,
                attachmentKey: selectedAttachmentKey,
              },
            });
          })
          .catch(err => this.toaster.error(err.message))
          .finally(() => (this.loading = false));
      };
      reader.readAsDataURL(attachment.fileObject);
    },
    fileSelectedStopExecution({ selectedAttachment: attachment, selectedAttachmentKey }) {
      this.loading = true;
      const reader = new FileReader();
      reader.onloadend = () => {
        var b64 = reader.result.replace(/^data:.+;base64,/, '');

        this.uploadAttachment({
          classification: 0,
          documentMetadata: {
            caseId: 0,
            caseCode: 0,
            requestId: 0,
            category: attachment.category,
            fileCategory: attachment.category,
          },
          fileCategory: attachment.category,
          extension: attachment.extension,
          base64FileContent: b64,
        })
          .then(({ data }) => {
            this.$set(this.formModel, 'stopExecutionAttachment', {
              fileData: {
                attachement: data,
                fileName: attachment.name,
                fileType: attachment.fileObject.type,
                sharePointId: data,
                extension: attachment.extension,
                fileCategory: attachment.category,
                attachmentKey: selectedAttachmentKey,
              },
            });
          })
          .catch(err => this.toaster.error(err.message))
          .finally(() => (this.loading = false));
      };
      reader.readAsDataURL(attachment.fileObject);
    },
    showDeleteObjectionNoteModal() {
      this.showDeleteObjectionNoteAttachment = true;
    },
    showDeleteObjectionStopExecutionModal() {
      this.showDeleteObjectionStopExecutionAttachment = true;
    },
    confirmDeleteObjectionNote() {
      this.$set(this.formModel, 'cassationFile', null);
      this.cancelDeleteObjectionNote();
    },
    confirmDeleteObjectionStopExecution() {
      this.$set(this.formModel, 'stopExecutionAttachment', null);
      this.cancelDeleteObjectionStopExecution();
    },
    cancelDeleteObjectionNote() {
      this.showDeleteObjectionNoteAttachment = false;
    },
    cancelDeleteObjectionStopExecution() {
      this.showDeleteObjectionStopExecutionAttachment = false;
    },
    viewAttachment() {
      if (this.formModel.cassationFile.fileData.hide == true) this.showAlertModalForCriminalCases = true;
      else this.showViewAttachmentModal = true;
    },
    viewStopExecutionAttachment() {
      if (this.formModel.stopExecutionAttachment.fileData.hide == true) this.showViewStopExecutionAttachmentModal = true;
      else this.showViewStopExecutionAttachmentModal = true;
    },
    hideViewAttachmentModal() {
      this.showViewAttachmentModal = false;
    },
    hideViewStopExecutionAttachmentModal() {
      this.showViewStopExecutionAttachmentModal = false;
    },
    readCurrentUserInfo() {
      if (this.userInfo) {
        const socialTypeId = this.userInfo.socialIdType;
        this.$set(this.formModel, 'socialIdType', socialTypeId);
        this.$set(this.formModel, 'socialId', this.userInfo.socialId);
        this.$set(this.formModel, 'firstName', this.userInfo.firstName);
        this.$set(this.formModel, 'fatherName', this.userInfo.fatherName);
        this.$set(this.formModel, 'grandFatherName', this.userInfo.grandFatherName);
        this.$set(this.formModel, 'familyName', this.userInfo.familyName);
        this.$set(this.formModel, 'dateOfBirth', this.userInfo.dateOfBirth);
        this.$set(
          this.formModel,
          'nationalityCountryId',
          socialTypeId === socialIdentityTypeEnum.nationalId ? countriesEnum.saudiArabia : this.userInfo.nationalityCountryId
        );
        this.$set(this.formModel, 'sex', this.userInfo.gender);
        this.$set(this.formModel, 'isRetrievedFromMoi', true);
        this.$set(this.formModel, 'fullName', this.userInfo.fullName);
        this.$set(this.formModel, 'relatedPartyID', this.userInfo.relatedPartyID);
      } else {
        this.$set(this.formModel, 'socialIdType', null);
        this.$set(this.formModel, 'socialId', null);
        this.$set(this.formModel, 'firstName', null);
        this.$set(this.formModel, 'fatherName', null);
        this.$set(this.formModel, 'grandFatherName', null);
        this.$set(this.formModel, 'familyName', null);
        this.$set(this.formModel, 'dateOfBirth', null);
        this.$set(this.formModel, 'nationalityCountryId', null);
        this.$set(this.formModel, 'sex', null);
        this.$set(this.formModel, 'isRetrievedFromMoi', false);
        this.$set(this.formModel, 'fullName', null);
        this.$set(this.formModel, 'relatedPartyID', null);
      }
    },
    stopExecutionChangeHandler() {
      if (this.formModel.stopExecution == yesOrNoEnumBooleanMap[yesOrNoEnumBoolean.no]) {
        this.formModel.reasonForStopExecution = '';
        this.formModel.stopExecutionEffect = '';
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.judgement-deed {
  display: flex;
  align-items: center;
  justify-content: center;
}

::v-deep .v-input--radio-group {
  .options-list {
    display: inline-grid !important;
  }
}

::v-deep .attachment-item {
  cursor: pointer;
}

.request-reasons-title {
  font-size: 18px;
}

.request-reason-label {
  font-weight: bold;
  font-size: 20px;
  color: #666;
  display: block;
  margin-bottom: 8px;
}

.request-reason-text {
  font-weight: bold;
  font-size: 17px;
  color: #333;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.memorandum-challenge-alert-container {
  .alert-notification {
    display: block;
    color: #f6c645;
    font-size: 14px;
  }

  .message {
    font-size: 16px;
    color: #faad14;
  }
}

.objection-judgment-confirm-text {
  color: darkred;
  font-size: 1rem;
  margin-bottom: 0;
}

.objection-note-attachment {
  display: flex;

  .objection-judgment-confirm-text {
    margin-left: 10px;
  }

  .uplaod-attachments-button .img-actions {
    width: 100% !important;
  }
}

.reason-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
}

::v-deep .attachment-item {
  cursor: pointer;
}
.blackColor {
  color: black;
}
.stop-execution-radio-col ::v-deep .v-input--radio-group .options-list {
  display: flex !important;
  flex-direction: row !important;
}
</style>
